import _PracticeHubScreenQuery from "../__generated__/PracticeHubScreenQuery.graphql";
import { Suspense, useCallback, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpenText, Dumbbell, Mic } from 'lucide-react';
import { En } from '../i18n/En';
import { formatDate } from '../i18n/format';
import { t } from '../i18n/t';
import { registerFor, type Register } from '../profile/register';
import { EmptyState } from './EmptyState';
import { HubDetail } from './HubDetail';
import { chips, emptyMessage, emptyWordsMessage, exemplarFor, filterFromParams, kidCard, kindLabel, paramsWithFilter, patternAbout, patternLabel, paramsWithSurface, surfaceFromParams, visibleItems, visibleWords, wordChips, type Chip, type KindFilter, type Surface } from './practice.ts';
import { nextPattern, patternRows, type PatternRow } from './progressOverview.ts';
import { DotLegend, NextUpCard, OutcomeDots, StatusBadge } from './ProgressSummary';
import { whenLabel } from './sessions.ts';
import { usePractise } from './usePractise';
import type { PracticeHubScreenQuery as PracticeHubScreenQueryType, PracticeHubScreenQuery$data } from '../__generated__/PracticeHubScreenQuery.graphql';
type HubLearner = NonNullable<NonNullable<PracticeHubScreenQuery$data['me']>['learner']>;
type HubItem = HubLearner['practiceItems'][number];
type HubWord = HubLearner['vocabularyItems']['edges'][number]['node'];

/**
 * One word the learner is picking up.
 *
 * A link to the word, and nothing else. There is no "Practise this" here, and
 * the absence is the design (vocabulary PRD §5): a pattern can steer a
 * conversation because the tutor's prompt carries pattern labels, but a word
 * cannot — sending one would put the learner's own vocabulary into the
 * tutor's prompt. What a word offers is hearing it, reading it and seeing it
 * used, on its own screen.
 */
function WordCard({
  word
}: {
  word: HubWord;
}) {
  return <Link to={`/practice/word/${encodeURIComponent(word.id)}`} className="border-base-200 bg-base-100 hover:border-primary/40 flex flex-col gap-1 rounded-box border p-4">
      <h3 className="leading-tight font-semibold">
        <En>{word.word}</En>
      </h3>
      <p className="text-base-content/50 text-xs">{t('practice-word-met', {
        count: word.metCount
      })}</p>
    </Link>;
}
function ChipRow({
  chips,
  selected,
  onSelect
}: {
  chips: Chip[];
  selected: KindFilter;
  onSelect: (k: KindFilter) => void;
}) {
  return <div className="flex flex-wrap gap-2">
      {chips.map(chip => <button key={chip.label} className={`btn btn-sm min-h-(--hit-min) ${selected === chip.kind ? 'btn-primary' : 'btn-ghost'}`} aria-pressed={selected === chip.kind} onClick={() => onSelect(chip.kind)}>
          {chip.label} ({chip.count})
        </button>)}
    </div>;
}

/**
 * One pattern the learner is working on, with its status and last outcomes
 * as the Progress screen shows them, read from the latest sessions.
 */
function PracticeCard({
  item,
  row,
  register,
  onPractise,
  onOpen,
  busy
}: {
  item: HubItem;
  row: PatternRow | undefined;
  register: Register;
  onPractise: (itemId: string) => void;
  onOpen: (itemId: string) => void;
  busy: boolean;
}) {
  if (register === 'kid') return <KidPracticeCard item={item} onPractise={onPractise} onOpen={onOpen} busy={busy} />;
  const exemplar = exemplarFor(item.exemplar, register);
  const about = patternAbout(item.pattern);
  const example = item.patternExample;
  return <article className="border-base-200 bg-base-100 hover:border-primary/40 flex flex-col gap-2 rounded-box border p-4">
      <button type="button" className="flex flex-col gap-2 text-start" onClick={() => onOpen(item.id)}>
        <header className="flex items-start justify-between gap-2">
          <h3 className="max-w-[80%] leading-tight font-semibold">{patternLabel(item.pattern)}</h3>
          <StatusBadge latest={row?.latest ?? null} />
        </header>
        {about && <p className="text-base-content/70 text-sm">{about}</p>}
        {exemplar ? <p className="text-base-content/70 text-sm">
            <En>
              <span className="line-through">{exemplar.learnerForm}</span>
              {' → '}
              <span className="font-medium">{exemplar.targetForm}</span>
            </En>
          </p> : about && example && <p className="text-sm font-medium">
              <span dir="ltr">{t('common-quoted', {
            text: example.text
          })}</span>
            </p>}
        <div className="flex items-end justify-between gap-2">
          <p className="text-base-content/50 text-xs">
            {kindLabel(item.kind)} · {t('practice-seen', {
            count: item.occurrences
          })}
            {/* Rendered only when there is one. "Never practised" is the ordinary
                state of a pattern a learner is working on, not a gap to label. */}
            {item.lastPractisedAt && ' ' + t('practice-last', {
            date: formatDate(new Date(item.lastPractisedAt))
          })}
          </p>
          <OutcomeDots recent={row?.recent ?? []} />
        </div>
      </button>
      {/* `practisable` is `startSession`'s own check. It is false at Pre-A1,
          where a tap would be refused with a code the client reads as the
          email gate. */}
      {item.practisable && <button className="btn btn-primary btn-sm min-h-(--hit-min) gap-2 self-start" disabled={busy} onClick={() => onPractise(item.id)}>
          <Mic aria-hidden className="size-4" />
          {busy ? t('common-starting') : t('practice-start')}
        </button>}
    </article>;
}

/**
 * One pattern in the kid register: a picture, its name and its example,
 * rendered from `kidCard` alone. No count, kind or date reaches it
 * (kid-practice §6): for a pattern, each is a record of the child's mistakes.
 */
function KidPracticeCard({
  item,
  onPractise,
  onOpen,
  busy
}: {
  item: HubItem;
  onPractise: (itemId: string) => void;
  onOpen: (itemId: string) => void;
  busy: boolean;
}) {
  const card = kidCard(item);
  const example = card.patternExample;
  return <article className="border-base-200 bg-base-100 flex flex-col gap-2 rounded border p-4">
      <button type="button" className="flex flex-col gap-2 text-start" onClick={() => onOpen(card.id)}>
        {example?.picture && <span aria-hidden className="text-4xl leading-none">
            {example.picture}
          </span>}
        <h3 className="leading-tight font-semibold">{patternLabel(card.pattern)}</h3>
        {example && <p className="font-medium">
            <En>{example.text}</En>
          </p>}
      </button>
      {card.practisable && <button className="btn btn-primary btn-sm min-h-(--hit-min) gap-2 self-start" disabled={busy} onClick={() => onPractise(card.id)}>
          <Mic aria-hidden className="size-4" />
          {busy ? t('common-starting') : t('practice-start')}
        </button>}
    </article>;
}

/**
 * The patterns a learner is working on, and the way into practising one.
 *
 * It does not teach. Its one action starts a conversation steered at a pattern,
 * which keeps every outcome evidence from real speech scored by the extraction
 * pipeline — the same reason §5 keeps the tutor from naming an error, and the
 * reason the "speak it" recorder this screen used to carry was deleted rather
 * than finished.
 */
export function PracticeHubScreen() {
  const data = useLazyLoadQuery<PracticeHubScreenQueryType>((_PracticeHubScreenQuery.hash && _PracticeHubScreenQuery.hash !== "689d15a9ea7f5d45552901ccbdb39e02" && console.error("The definition of 'PracticeHubScreenQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _PracticeHubScreenQuery), {},
  // The learner comes back here after a conversation to find what changed.
  // Served from the store, that is the list they left.
  {
    fetchPolicy: 'store-and-network'
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    practise,
    busy,
    why
  } = usePractise();
  const practisePattern = useCallback((id: string) => practise({
    practiceItemId: id
  }), [practise]);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const learner = data.me?.learner;
  const items: readonly HubItem[] = learner?.practiceItems ?? [];
  const words: readonly HubWord[] = learner?.vocabularyItems.edges.map(e => e.node) ?? [];
  // The same inference every register-gated screen makes (`AccountHistory`,
  // `ProgressView`): the character is the mode. It gates content here, not
  // only looks — §8 keeps corrected words off a child's list — and the gate
  // itself is `visibleWords`, tested, not a condition in this JSX.
  const register = registerFor(learner?.defaultCharacter?.audience);
  const surface: Surface = surfaceFromParams(searchParams);
  const setSurface = (next: Surface) => setSearchParams(paramsWithSurface(searchParams, next), {
    replace: true
  });

  // The filter lives in the URL rather than in state, because a debrief's focus
  // area links straight to `/practice?focus=LEXIS`. One filter, two entry
  // points -- the screen used to have a chip row *and* a separate "showing
  // LEXIS / View all" banner for the deep link, which were two mechanisms for
  // one idea. The mapping itself is in `practice.ts` so it can be tested
  // without a DOM; this reads it.
  //
  // `replace`, so flipping between chips does not build a Back stack of filter
  // states the learner has to walk out of to leave the screen.
  const kind: KindFilter = filterFromParams(searchParams);
  const setKind = (next: KindFilter) => setSearchParams(paramsWithFilter(searchParams, next), {
    replace: true
  });
  const sessions = learner?.sessions?.nodes ?? [];
  const rows = register === 'adult' ? patternRows(items, sessions, items.length) : [];
  const rowById = new Map(rows.map(r => [r.id, r]));
  const next = register === 'adult' ? nextPattern(items, sessions) : {
    kind: 'talk'
  } as const;
  const lastTalk = sessions.reduce<string | null>((latest, s) => !latest || s.startedAt > latest ? s.startedAt : latest, null);
  const filtered = visibleItems(items, kind);
  const shownWords = visibleWords(words, kind, register);
  const allWords = visibleWords(words, null, register);
  return <div className="flex flex-col gap-4 py-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">{t('practice-title')}</h1>
        <p className="text-base-content/60 text-sm">
          {surface === 'words' ? allWords.length === 0 ? t('practice-words-none') : t('practice-words-count', {
          count: allWords.length
        }) : items.length === 0 ? t('practice-patterns-none') : t('practice-patterns-count', {
          count: items.length
        })}
        </p>
      </header>

      {why && <div role="alert" className="alert alert-error">
          <span>{why}</span>
        </div>}

      {surface === 'patterns' && next.kind === 'practise' && kind === null && <NextUpCard next={next} rows={rows} lastTalk={lastTalk ? whenLabel(lastTalk, new Date()) : null} onPractise={practisePattern} busy={busy} />}

      {/* A floor under each tab: in Vietnamese "Words" is "Từ", one short
          syllable, and the tab shrank to its width. */}
      {/* Two kinds of thing over one filtering mechanism (vocabulary PRD §5):
          the chip row below switches its chips with the surface. */}
      <div role="tablist" aria-label={t('practice-surface-label')} className="tabs tabs-box self-start">
        {(['patterns', 'words'] as const).map(s => <button key={s} role="tab" aria-selected={surface === s} className={`tab min-w-28 ${surface === s ? 'tab-active' : ''}`} onClick={() => setSurface(s)}>
            {t(s === 'patterns' ? 'practice-surface-patterns' : 'practice-surface-words')}
          </button>)}
      </div>

      {surface === 'patterns' ? <>
          {/* No kind chips for a child: each counts their mistakes of that
              kind (kid-practice §6). */}
          {items.length > 0 && register === 'adult' && <ChipRow chips={chips(items)} selected={kind} onSelect={setKind} />}
          {rows.some(r => r.recent.length > 0) && <DotLegend />}

          {items.length === 0 ? <EmptyState icon={Dumbbell} body={emptyMessage(null)} action={{
        to: '/session',
        label: t('progress-next-talk')
      }} /> : filtered.length === 0 ? <p className="text-base-content/50 py-12 text-center">{emptyMessage(kind)}</p> : <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map(item => <PracticeCard key={item.id} item={item} row={rowById.get(item.id)} register={register} onPractise={practisePattern} onOpen={setSelectedItemId} busy={busy} />)}
            </div>}
        </> : <>
          {allWords.length > 0 && <ChipRow chips={wordChips(words, register)} selected={kind} onSelect={setKind} />}

          {allWords.length === 0 ? <EmptyState icon={BookOpenText} body={emptyWordsMessage(null)} action={{
        to: '/session',
        label: t('progress-next-talk')
      }} /> : shownWords.length === 0 ? <p className="text-base-content/50 py-12 text-center">{emptyWordsMessage(kind)}</p> : <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {shownWords.map(word => <WordCard key={word.id} word={word} />)}
            </div>}
        </>}

      {selectedItemId &&
    // Its own boundary, so opening a card does not blank the grid behind
    // it while the detail's query resolves.
    <Suspense fallback={null}>
          <HubDetail itemId={selectedItemId} onDismiss={() => setSelectedItemId(null)} onPractise={practisePattern} busy={busy} why={why} />
        </Suspense>}
    </div>;
}